import { Badge } from "../ui/Badge";
import { surfaceLabel } from "../../lib/campaign-surfaces";
import { isBlockingReviewAlert } from "../../../../shared/review-source-risk";
import type { ReviewApprovalItem } from "../../../../shared/types";

function formatAge(value: string | null | undefined): string {
  if (!value) return "recently";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "recently";
  const diffMs = Date.now() - parsed.getTime();
  const diffMinutes = Math.max(0, Math.floor(diffMs / 60000));
  if (diffMinutes < 60) return `${diffMinutes || 1}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

function formatRowStaleness(brief: ReviewApprovalItem): string {
  return `Created ${formatAge(brief.created_at)} · Updated ${formatAge(brief.updated_at)}`;
}

function nextActionForBrief(brief: ReviewApprovalItem): string {
  if (brief.published_url?.trim()) return "Follow up on published URL";
  if (brief.state === "approved" || brief.state === "scheduled") return "Publish or record URL";
  if (brief.provenance_alerts?.some(isBlockingReviewAlert)) {
    return "Inspect source risk";
  }
  return "Approve or request changes";
}

export function ReviewQueueRow({
  brief,
  isSelected,
  onSelect,
}: {
  brief: ReviewApprovalItem;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const channel = surfaceLabel(brief.surface ?? brief.platform ?? undefined);
  const status = brief.state.replace(/_/g, " ");
  const nextAction = nextActionForBrief(brief);
  const staleness = formatRowStaleness(brief);

  return (
    <button
      type="button"
      id={`queue-row-${brief.id}`}
      aria-selected={isSelected}
      data-selected={isSelected ? "true" : "false"}
      onClick={onSelect}
      className={`w-full rounded-2xl border p-3 text-left transition ${
        isSelected
          ? "border-slate-900 bg-slate-950 text-white shadow-sm"
          : "border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-50"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{brief.title}</p>
          <p className={`mt-1 text-xs ${isSelected ? "text-slate-300" : "text-slate-500"}`}>
            {channel} · {staleness}
          </p>
        </div>
        <Badge variant={brief.state === "in_review" ? "warning" : "info"}>{status}</Badge>
      </div>
      <p className={`mt-3 text-xs font-medium ${isSelected ? "text-slate-200" : "text-slate-700"}`}>
        Next: {nextAction}
      </p>
    </button>
  );
}
